import React from "react";
import * as Icons from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { FeatureCard } from "@/components/blocks/FeatureCard";
import { CtaBand } from "@/components/blocks/CtaBand";
import { Reveal } from "@/components/common/Reveal";

const WHAT = [
  { icon: "MessageSquareText", title: "WhatsApp Automation", desc: "Automate conversations, notifications and follow-ups on WhatsApp." },
  { icon: "Send", title: "Bulk WhatsApp Campaigns", desc: "Run compliant, permission-based campaigns at scale." },
  { icon: "MessageCircle", title: "Bulk SMS", desc: "Promotional, transactional and OTP messaging with reporting." },
  { icon: "PhoneCall", title: "Smart Telephony", desc: "Cloud calling with IVR, routing, recording and analytics." },
];

const WHY = [
  { icon: "Layers", title: "One platform, many channels", desc: "WhatsApp, SMS and voice — combined into a single communication strategy." },
  { icon: "Workflow", title: "Automation at the core", desc: "Reduce repetitive work and respond to customers faster." },
  { icon: "ShieldCheck", title: "Responsible communication", desc: "We champion permission-based, compliant messaging practices." },
  { icon: "Headphones", title: "Support that stays", desc: "Guidance during onboarding and throughout your journey." },
];

export default function About() {
  useSEO({
    title: "About E Telecom | Business Communication & Automation",
    description: "E Telecom provides business communication technologies combining WhatsApp, SMS, automation and smart telephony to help businesses communicate better.",
    path: "/about",
  });

  return (
    <>
      <PageHero
        eyebrow="About E Telecom"
        icon="Building2"
        title="Helping Businesses Communicate Better"
        description="E Telecom provides business communication technologies that combine messaging, automation and telephony — so businesses can connect with customers faster and at scale."
        primary={{ to: "/contact", label: "Talk to an Expert" }}
        secondary={{ to: "/pricing", label: "Request a Quote" }}
      />

      {/* Mission & Vision */}
      <section className="et-section bg-white">
        <div className="et-container grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="et-card h-full p-8">
              <span className="et-icon-box"><Icons.Compass className="h-5 w-5" /></span>
              <h2 className="font-display text-xl font-semibold text-slate-900 mt-4">Our Mission</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">To make smarter business communication accessible to every organisation — helping teams reach customers, automate repetitive work and drive more meaningful engagement across WhatsApp, SMS and voice.</p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="et-card h-full p-8">
              <span className="et-icon-box"><Icons.Eye className="h-5 w-5" /></span>
              <h2 className="font-display text-xl font-semibold text-slate-900 mt-4">Our Vision</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">To be a trusted technology partner for customer communication — enabling businesses of every size to communicate responsibly, efficiently and at scale.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we do */}
      <section className="et-section">
        <div className="et-container">
          <SectionHeading eyebrow="What We Do" title="A single platform for customer communication" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHAT.map((w, i) => <FeatureCard key={w.title} icon={w.icon} title={w.title} description={w.desc} delay={(i % 4) * 0.05} />)}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="et-section bg-white">
        <div className="et-container">
          <SectionHeading eyebrow="Why Businesses Choose E Telecom" title="Communication built around your business" />
          <div className="mt-12 grid sm:grid-cols-2 gap-5">
            {WHY.map((w, i) => <FeatureCard key={w.title} icon={w.icon} title={w.title} description={w.desc} delay={(i % 2) * 0.05} />)}
          </div>
        </div>
      </section>

      <CtaBand title="Let's build your communication strategy" subtitle="Tell us your requirement and we'll help you choose the right mix of WhatsApp, SMS and telephony." />
    </>
  );
}
